/**
 * Clients Service - Handles all client-related API calls
 */

// Query Keys
const AVAILABLE_CLIENT_TABS_QUERY_KEY = 'AVAILABLE_CLIENT_TABS_QUERY'
const CLIENTS_QUERY_KEY = 'CLIENTS_QUERY'
const CLIENT_QUERY_KEY = 'CLIENT_QUERY'

// GraphQL Query Strings
const availableClientTabsQuery = `
  query AvailableClientTabs {
    availableClientTabs {
      tabs {
        value
        label
        enabled
        count
        description
      }
    }
  }
`

const clientsQuery = `
  query Clients(
    $search: String
    $cursor: String
    $pageSize: Float
    $siteId: String
    $filters: ClientFilters
    $orderBy: [ClientsOrderByInput!]
  ) {
    clients(
      search: $search
      cursor: $cursor
      pageSize: $pageSize
      siteId: $siteId
      filters: $filters
      orderBy: $orderBy
    ) {
      results {
        row {
          id
          firstName
          lastName
          name
          email
          mobilePhoneNumber
          homePhoneNumber
          gender
          occupation
          dateOfBirth
          barcode
          fobNumber
          firebaseId
          profile
          membershipStatus
          isSuspended
          joinDate
          lastVisit
          lastBooking
          createdAt
          updatedAt
          homeSiteId
          homeSiteName
          notes
          address {
            unit
            streetNumber
            streetAddress
            city
            state
            zipCode
            country
          }
          emergencyContact {
            name
            phone
            email
            relationship
          }
          communicationPreferences {
            email
            sms
            phone
            notifications
          }
          introOffersClaimed {
            offerId
            siteId
            claimedAt
            usedAt
          }
          profileImage(input: { format: webp, type: thumbnail }) {
            url
          }
          alerts {
            clientId
            type
            requiresFetch
            data {
              count
              failed
              outstanding
              notes
              properties
              dateOfBirth
              address
              homeSiteName
              isSuspended
            }
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        startCursor
        endCursor
        totalRecords
      }
      permissions {
        canChangePassword
        canEdit
        canDelete
      }
    }
  }
`

const clientQuery = `
  query Client($id: String!, $siteId: String!, $staffUserId: String!) {
    client(id: $id, siteId: $siteId, staffUserId: $staffUserId) {
      client {
        id
        firstName
        lastName
        name
        email
        mobilePhoneNumber
        homePhoneNumber
        gender
        occupation
        dateOfBirth
        address {
          unit
          streetNumber
          streetAddress
          city
          state
          zipCode
          country
        }
        barcode
        fobNumber
        customerId
        emergencyContact {
          name
          phone
          email
          address {
            number
            street
            city
            state
            zipCode
            country
          }
          relationship
        }
        communicationPreferences {
          email
          sms
          phone
          notifications
        }
        homeSiteId
        homeSiteName
        smartGroups {
          id
          name
          isVisible
          sortOrder
        }
        siteRelationships {
          siteId
          joinDate
          siteSpecificData {
            enrollmentSource
            membershipType
            notes
            preferences {
              notifications
              communications
            }
          }
          createdAt
          updatedAt
          lastBillingDate
          lastAttendanceDate
          lastBookingDate
        }
        relations {
          id
          name
          email
          status
        }
        joinDate
        createdAt
        updatedAt
      }
      permissions {
        canChangePassword
        canEdit
        canDelete
      }
    }
  }
`

// Mutation Query Strings
const createClientMutation = `
  mutation CreateClient($input: ClientCreateInput!, $siteId: String) {
    createClient(input: $input, siteId: $siteId) {
      id
    }
  }
`

const updateClientMutation = `
  mutation UpdateClient($id: String!, $input: ClientUpdateInput!) {
    updateClient(id: $id, input: $input) {
      success
    }
  }
`

const deleteClientMutation = `
  mutation DeleteClient($id: String!) {
    deleteClient(id: $id) {
      success
    }
  }
`

const changeClientPasswordMutation = `
  mutation UpdatePassword($id: String!, $input: ChangePasswordInput!, $siteId: String!) {
    updatePassword(id: $id, input: $input, siteId: $siteId) {
      success
      message
    }
  }
`

// Export all query keys
export { AVAILABLE_CLIENT_TABS_QUERY_KEY, CLIENTS_QUERY_KEY, CLIENT_QUERY_KEY }

// Export all queries and mutations
export {
  availableClientTabsQuery,
  clientsQuery,
  clientQuery,
  createClientMutation,
  updateClientMutation,
  deleteClientMutation,
  changeClientPasswordMutation,
}
